import type React from 'react';

import { type ContentKitElementProps, resolveDynamicBinding } from '@gitbook/contentkit-react';
import type { ContentKitSelectSourceOpenAPISpec } from '@gitbook/contentkit-react/src/elements/ContentKitSelect';
import type { StyleProp, ViewStyle } from '@gitbook/react-uikit';
import { Button, DialogTrigger } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useAssertCurrentOrganizationId } from '../../state';
import { OpenAPISpecSelector } from '../OpenAPI';

/**
 * Component that renders a select input for OpenAPI specifications.
 */
export function ContentKitSelectOpenAPISpec(
    props: ContentKitElementProps<ContentKitSelectSourceOpenAPISpec> & {
        style?: StyleProp<ViewStyle>;
    }
): React.ReactElement {
    const { element, localState, setLocalState, dispatchAction } = props;

    if (props.element.multiple) {
        throw new Error('OpenAPI select does not support multiple selection');
    }

    if (props.element.initialValue) {
        throw new Error('OpenAPI select does not support initial value');
    }

    const organizationId = useAssertCurrentOrganizationId();

    const loadable = useLoadingState(false);
    const [, wrapDispatching] = loadable;

    const onValueChange = (updatedState: object) => {
        const onChangeAction = element.onValueChange;
        if (onChangeAction) {
            const action = resolveDynamicBinding(updatedState, onChangeAction);
            wrapDispatching(() => dispatchAction(action));
        }
    };

    const specSlug = localState[element.state] ?? element.initialValue ?? null;

    return (
        <OpenAPISpecSelector
            organizationId={organizationId}
            onChange={(spec) => {
                setLocalState((prevState) => {
                    const updated = {
                        ...prevState,
                        [element.state]: spec.slug,
                    };
                    onValueChange(updated);
                    return updated;
                });
            }}
            analyticsContext={{
                triggerLocation: 'contentkit-select',
            }}
        >
            {({ triggerProps, openAPISpecs, addOpenAPISpecTrigger }) => {
                const isValid = openAPISpecs.some((spec) => spec.slug === specSlug);
                const value = isValid ? specSlug : null;
                if (value || openAPISpecs.length > 0) {
                    return (
                        <Button
                            {...triggerProps}
                            kind="secondary"
                            label={value ?? 'Select specification'}
                            disabled={element.disabled}
                            loadable={loadable}
                        />
                    );
                }
                return (
                    <DialogTrigger trigger={addOpenAPISpecTrigger}>
                        <Button
                            kind="secondary"
                            label="Add specification"
                            disabled={element.disabled}
                            loadable={loadable}
                        />
                    </DialogTrigger>
                );
            }}
        </OpenAPISpecSelector>
    );
}
